/*eslint-env webextensions*/
import { eventChannel } from 'redux-saga'
import {
  call,
  put,
  select,
  take,
  takeEvery
} from 'redux-saga/effects'

import {
  clearSong,
  resetTimer,
  setOffset,
  startTimer,
  updateSong,
  updateStatus
} from '../duck/operations'

import placeholderImage from '../assets/images/placeholder-thumbnail.jpeg'

import types from '../duck/types'
import states from '../lib/states'

import {
  isRunningAsElectronApp,
  isRunningAsLocalSite
} from '../lib/environmentUtils'

let port = undefined

let ws = undefined
const wsURL = 'ws://localhost:9932'
const wsProtocols = ['rust-websocket']

function initCommunicationChannel() {
  return eventChannel(emitter => {
    // Open connection to the background script (if run as a browser extension) or to the web socket (if run as an electron app)
    if (isRunningAsElectronApp() || isRunningAsLocalSite()) {
      connectToWebSocket(emitter)
    } else {
      connectToExtension(emitter)
    }
    // unsubscribe function
    return (() => {
      // TODO: close connection here, not required for now as we never cancel this channel
      return null
    })
  })
}

function connectToWebSocket(emitter) {
  ws = new WebSocket(wsURL, wsProtocols)
  ws.onmessage = (message) => {
    handleIncomingMessage(emitter, JSON.parse(message.data))
  }
  ws.onclose = () => {
    handleWebSocketError(emitter)
  }
}

function handleWebSocketError(emitter) {
  ws = undefined
  emitter(updateStatus(states.MISSING))
  setTimeout(() => {
    connectToWebSocket(emitter)
  }, 1000)
}

function sendOnWebSocket(msg) {
  if (ws !== undefined && ws.readyState === 1) {
    ws.send(msg)
  }
}

function connectToExtension(emitter) {
  const extensionId = process.env.NODE_ENV !== 'development'
    ? process.env.REACT_APP_EXTENSION_ID_PROD
    : process.env.REACT_APP_EXTENSION_ID_DEV

  try {
    chrome.runtime.sendMessage(extensionId, {}, (response) => {
      if (response === undefined) {
        handleExtensionError(emitter, states.MISSING)
        return
      }
      port = chrome.runtime.connect(extensionId)
      port.onMessage.addListener((msg) => {
        handleIncomingMessage(emitter, msg)
      })
      port.onDisconnect.addListener(() => {
        handleExtensionError(emitter, states.DISCONNECTED)
        return
      })
    })
  } catch (error) {
    handleExtensionError(emitter, states.MISSING)
    return
  }
}

function handleExtensionError(emitter, status) {
  port = undefined
  emitter(updateStatus(status))
  setTimeout(() => {
    connectToExtension(emitter)
  }, 1000)
}

function handleIncomingMessage(emitter, msg) {
  if (!msg || !msg.player) {
    return
  }

  // Offset may have changed: emit a message. The reducer will take care of verifying if an update is needed
  emitter(setOffset(msg.player.offset ? msg.player.offset : 0))

  const metadata = msg.player.playback_metadata

  if (!metadata || (!metadata.artist && !metadata.artwork_url && !metadata.title)) {
    let status = msg.player.status
    if (metadata && metadata.source === 'sync_tone') {
      // Special case: AutoSync™
      status = states.AUTOSYNC
    } else if (msg.player.status === states.PLAYING || msg.player.status === states.PAUSED) {
      // Special case: discontinuity in the player. Force the status to "BUFFERING"
      status = states.BUFFERING
    }
    return [
      emitter(resetTimer()),
      emitter(clearSong()),
      emitter(updateStatus(status))
    ]
  }

  const songInfo = {
    ...metadata,
    position: Math.max(Math.round((msg.time.current_time - metadata.from_time) / 1000), 0),
    duration: Math.round((metadata.duration) / 1000),
  }

  if (!metadata.title) {
    songInfo.title = 'Unknown Song'
  }
  if (!metadata.artist) {
    songInfo.artist = 'Unkown Artist'
  }
  if (!metadata.artwork_url) {
    songInfo.artwork_url = placeholderImage
  }

  switch(msg.player.status) {
  case states.PLAYING:
    return [
      emitter(resetTimer()),
      emitter(startTimer(songInfo.position, songInfo.duration)),
      emitter(updateSong(songInfo, msg.player.status))
    ]

  case states.BUFFERING:
  case states.PAUSED:
    return [
      emitter(resetTimer()),
      emitter(updateSong(songInfo, msg.player.status))
    ]

  default:
    return [
      emitter(updateStatus(msg.player.status))
    ]
  }
}

export function* communicationChannelHandler() {
  const channel = yield call(initCommunicationChannel)
  while (true) {
    const action = yield take(channel)
    yield put(action)
  }
}

function* getStateAsync() {
  if (port !== undefined) {
    yield port.postMessage({ type: types.GET_STATE })
  } else if (ws !== undefined) {
    yield sendOnWebSocket(JSON.stringify({ type: types.GET_STATE }))
  }
}

export function* watchGetState() {
  yield takeEvery(types.GET_STATE, getStateAsync)
}

function* setOffsetAsync() {
  const state = yield select()
  if (port !== undefined) {
    yield port.postMessage({ type: types.SET_OFFSET, offset: state.offset })
  } else if (ws !== undefined) {
    yield sendOnWebSocket(JSON.stringify({ type: types.SET_OFFSET, offset: state.offset }))
  }
}

export function* watchOffsetChange() {
  yield takeEvery([types.INCREMENT_OFFSET, types.DECREMENT_OFFSET], setOffsetAsync)
}

function* playerStopAsync() {
  if (port !== undefined) {
    yield port.postMessage({ type: types.PLAYER_STOP })
  } else if (ws !== undefined) {
    yield sendOnWebSocket(JSON.stringify({ type: types.PLAYER_STOP }))
  }
}

export function* watchPlayerStop() {
  yield takeEvery(types.PLAYER_STOP, playerStopAsync)
}

function* disconnectPlayerAsync() {
  if (port !== undefined) {
    yield port.postMessage({ type: types.PLAYER_STOP })
  } else if (ws !== undefined) {
    yield sendOnWebSocket(JSON.stringify({ type: types.PLAYER_STOP }))
  }
}

export function* watchDisconnect() {
  yield takeEvery(types.PLAYER_STOP, disconnectPlayerAsync)
}



// WEBPACK FOOTER //
// ./src/sagas/communicationSaga.js